import { TypeChecker } from '@attain-apps/type-guards'

import { Attribute } from './attribute'
import { Dimension } from './dimension'
import { Entity } from './entity'
import { Enum } from './enum'
import type { Id as _Id } from './id'
import type { Model } from './model'
import type { Of } from './of'
import { Purpose } from './purpose'
import { Snapshot } from './snapshot'
import { Structure } from './structure'
import { Type } from './type'

/**
 * Represents any valid implementation of {@link Model}
 *
 * Can be used as a discriminated union
 */
export type Any = Of<Type>
export namespace Any {
  /**
   * Identifier of {@link Any}
   *
   * Since each {@link Type} has its own requirements for its IDs, this
   * should be a union of only the IDs that match the requirements of at least
   * one {@link Type}. This is in contrast to {@link Id} which is
   * anything that follows the general structure of a {@link Model} ID, even if
   * they don't meet the requirements of any {@link Type}
   */
  export type Id = Any['id']
  export namespace Id {
    export const isId = TypeChecker.Factory.union(
      ...Object.values({
        [Type.Attribute]: Attribute.Id.isId,
        [Type.Dimension]: Dimension.Id.isId,
        [Type.Entity]: Entity.Id.isId,
        [Type.Enum]: Enum.Id.isId,
        [Type.Purpose]: Purpose.Id.isId,
        [Type.Snapshot]: Snapshot.Id.isId,
        [Type.Structure]: Structure.Id.isId,
      } as const satisfies {
        // NOTE: this ensures we don't forget any type checkers
        [TType in Type]: TypeChecker<_Id<TType>>
      }),
    )
  }
}
